import { ReactNode } from 'react';
import styled from 'styled-components';
import { Tooltip } from 'ui/tooltip';
import { WarningNoFill } from 'ui/icons';
import { CollapsibleContentArrow } from 'ui/collapsibleContent';
import { colors, spacing } from 'ui/theme';
import { CollapseButton } from 'ui/treeView/TreeBranch';
import { pxToRem } from 'utils/convert';

export const VARIANT_COUNT_WARNING_TEST_ID = 'variant-count-warning';
export const CAMPAIGN_COUNT_WARNING_TEST_ID = 'campaign-count-warning';
export const DEPTH_LIMIT_WARNING_TEST_ID = 'depth-limit-warning';

const VARIANT_COUNT_WARNING =
  'Only aggregate metrics are available for dynamic URLs containing more than 50 variants.';
const CAMPAIGN_COUNT_WARNING =
  'If more than 50 Campaigns use this snippet, only the first 50 can be displayed in this table.';
const DEPTH_LIMIT_WARNING = 'Too many nested Snippets are in use to display the underlying URLs.';

const StyledWarning = styled(WarningNoFill)`
  path {
    stroke: ${colors.textSecondary};
    stroke-width: 0;
  }
`;

const NonExpandableLimitWarning = styled.div`
  align-items: center;
  display: flex;
  color: ${colors.textSecondary};
  gap: ${spacing.half};
  margin-right: ${pxToRem(6)};
`;

const StyledTooltip = styled(Tooltip)`
  align-items: center;
  display: flex;
  svg {
    margin-bottom: ${spacing.single};
  }
`;

type ExpansionTriggerProps = {
  isCollapsed: (path: string) => boolean;
  isExpandable?: boolean;
  isAtDepthLimit?: boolean;
  isAtCampaignLimit?: boolean;
  path: string;
  toggleLabel?: ReactNode;
};

export const ExpansionTrigger = ({
  isCollapsed,
  isExpandable,
  isAtDepthLimit,
  isAtCampaignLimit,
  path,
  toggleLabel,
}: ExpansionTriggerProps) => {
  if (isExpandable) {
    return (
      <CollapseButton>
        {toggleLabel}
        {isAtCampaignLimit && (
          <StyledTooltip tooltipContent={CAMPAIGN_COUNT_WARNING}>
            <StyledWarning
              color={colors.textSecondary}
              data-test={CAMPAIGN_COUNT_WARNING_TEST_ID}
            />
          </StyledTooltip>
        )}
        <CollapsibleContentArrow
          isCollapsed={isCollapsed(path)}
          height={16}
          width={16}
          arrowStart={0}
          arrowEnd={180}
        />
      </CollapseButton>
    );
  }

  return (
    <NonExpandableLimitWarning>
      <span>{toggleLabel}</span>
      <StyledTooltip tooltipContent={isAtDepthLimit ? DEPTH_LIMIT_WARNING : VARIANT_COUNT_WARNING}>
        <WarningNoFill
          color={colors.textSecondary}
          data-test={isAtDepthLimit ? DEPTH_LIMIT_WARNING_TEST_ID : VARIANT_COUNT_WARNING_TEST_ID}
        />
      </StyledTooltip>
    </NonExpandableLimitWarning>
  );
};
